import { useState } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence, type Variants } from "framer-motion";
import { styled } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import InputLabel from "@mui/material/InputLabel";
import FormControl from "@mui/material/FormControl";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import CircularProgress from "@mui/material/CircularProgress";
import { Heart, Check } from "lucide-react";
import { supabase } from "../../supabaseClient";
import Petals from "../common/Petals";
import Flourish from "../common/Flourish";
import SplitText from "../common/SplitText";
import { fonts, palette } from "../../theme";

const EASE = [0.16, 1, 0.3, 1] as const;

// ── Styled ────────────────────────────────────────────────────

const Section = styled("section")({
  position: "relative",
  overflow: "hidden",
  padding: "110px 20px 130px",
  background:
    "radial-gradient(120% 70% at 50% 8%, #FFFDF9 0%, #FBF4E9 45%, #F4E9D6 100%)",
  "@media (max-width:600px)": { padding: "80px 14px 96px" },
});

// Lueur ambiante douce
const Bloom = styled(Box)({
  position: "absolute",
  top: "10%",
  left: "50%",
  width: 560,
  height: 560,
  transform: "translateX(-50%)",
  borderRadius: "50%",
  background:
    "radial-gradient(circle, rgba(231,216,196,0.5) 0%, rgba(231,216,196,0) 65%)",
  pointerEvents: "none",
  zIndex: 0,
});

const Container = styled(Box)({ position: "relative", zIndex: 2, textAlign: "center" });

const Header = styled(motion.div)({ maxWidth: 540, margin: "0 auto 36px" });

const Title = styled(Typography)({
  fontFamily: fonts.display,
  fontSize: "clamp(2rem, 5vw, 3rem)",
  fontWeight: 600,
  letterSpacing: "0.3em",
  textTransform: "uppercase",
  color: palette.ink,
});

const Subtitle = styled(Typography)({
  fontFamily: fonts.serif,
  fontSize: "1.05rem",
  lineHeight: 1.95,
  color: palette.warmGray,
  marginTop: "16px",
});

// Carte premium en verre
const FormCard = styled(motion.div)({
  position: "relative",
  // Repli fiable (Samsung Internet, anciens Android, mode éco d'énergie) :
  // fond opaque par défaut, effet verre uniquement si backdrop-filter est supporté.
  backgroundColor: "rgba(255,255,255,0.94)",
  border: "1px solid rgba(191,160,106,0.32)",
  borderRadius: 22,
  boxShadow:
    "0 24px 60px rgba(58,53,47,0.12), inset 0 1px 0 rgba(255,255,255,0.6)",
  maxWidth: 780,
  margin: "0 auto",
  padding: "44px",
  textAlign: "left",
  overflow: "hidden",
  "@supports ((backdrop-filter: blur(1px)) or (-webkit-backdrop-filter: blur(1px)))": {
    backgroundColor: "rgba(255,255,255,0.55)",
    backdropFilter: "blur(16px)",
    WebkitBackdropFilter: "blur(16px)",
  },
  "@media (max-width:600px)": { padding: "26px" },
});

const TwoCol = styled(Box)({
  display: "flex",
  gap: "28px",
  "@media (max-width:700px)": { flexDirection: "column" },
});

const LeftCol = styled(Box)({ flex: 1, display: "flex", flexDirection: "column", gap: "20px" });
const RightCol = styled(Box)({ flex: 1, display: "flex", flexDirection: "column" });

// Champs luxe (focus doré, hover glow)
const StyledTextField = styled(TextField)({
  "& .MuiOutlinedInput-root": {
    borderRadius: 12,
    backgroundColor: "rgba(255,255,255,0.65)",
    fontFamily: fonts.serif,
    fontSize: "1rem",
    transition: "box-shadow .35s ease, background-color .35s ease",
    "& fieldset": { borderColor: "rgba(191,160,106,0.35)", transition: "border-color .3s ease" },
    "&:hover fieldset": { borderColor: "rgba(191,160,106,0.7)" },
    "&:hover": { boxShadow: "0 4px 14px rgba(191,160,106,0.14)" },
    "&.Mui-focused fieldset": { borderColor: palette.gold, borderWidth: 2 },
    "&.Mui-focused": {
      boxShadow: "0 6px 22px rgba(191,160,106,0.26)",
      backgroundColor: "rgba(255,255,255,0.9)",
    },
  },
  "& .MuiInputLabel-root": {
    fontFamily: fonts.sans,
    fontSize: "0.8rem",
    letterSpacing: "0.05em",
    color: palette.warmGray,
  },
  "& .MuiInputLabel-root.Mui-focused": { color: palette.goldDeep },
});

const selectSx = {
  borderRadius: "12px",
  backgroundColor: "rgba(255,255,255,0.65)",
  fontFamily: fonts.serif,
  fontSize: "1rem",
  transition: "box-shadow .35s ease",
  "& fieldset": { borderColor: "rgba(191,160,106,0.35)" },
  "&:hover fieldset": { borderColor: "rgba(191,160,106,0.7)" },
  "&.Mui-focused fieldset": { borderColor: palette.gold, borderWidth: 2 },
  "&.Mui-focused": { boxShadow: "0 6px 22px rgba(191,160,106,0.26)" },
};

const GuestBox = styled(Box)({
  backgroundColor: "rgba(255,255,255,0.5)",
  border: "1px solid rgba(191,160,106,0.28)",
  borderRadius: 12,
  padding: "16px 18px",
});

const GuestTitle = styled(Typography)({
  fontFamily: fonts.sans,
  fontSize: "0.72rem",
  letterSpacing: "0.12em",
  textTransform: "uppercase",
  color: palette.warmGray,
  marginBottom: "8px",
});

const MessageField = styled(StyledTextField)({
  "& .MuiOutlinedInput-root": {
    // fond "papier" très discret
    backgroundImage:
      "repeating-linear-gradient(transparent, transparent 31px, rgba(191,160,106,0.10) 32px)",
  },
});

// Bouton centerpiece
const SubmitBtn = styled(motion.button)({
  position: "relative",
  overflow: "hidden",
  alignSelf: "center",
  marginTop: "32px",
  border: "none",
  cursor: "pointer",
  padding: "15px 56px",
  borderRadius: 40,
  fontFamily: fonts.sans,
  fontSize: "0.82rem",
  letterSpacing: "0.24em",
  textTransform: "uppercase",
  color: "#fff",
  background: "linear-gradient(135deg, #CDAC70 0%, #A8884F 100%)",
  boxShadow: "0 12px 28px rgba(168,136,79,0.42)",
  "&::after": {
    content: '""',
    position: "absolute",
    top: 0,
    left: "-70%",
    width: "45%",
    height: "100%",
    background:
      "linear-gradient(120deg, transparent, rgba(255,255,255,0.55), transparent)",
    transform: "skewX(-20deg)",
    transition: "left .75s ease",
  },
  "&:hover::after": { left: "130%" },
  "&:disabled": { cursor: "default", opacity: 0.85 },
});

const ErrorMsg = styled(Box)({
  fontFamily: fonts.sans,
  fontSize: "0.85rem",
  color: "#a23b3b",
  backgroundColor: "rgba(162,59,59,0.08)",
  border: "1px solid rgba(162,59,59,0.25)",
  borderRadius: 10,
  padding: "10px 14px",
  marginBottom: "18px",
  textAlign: "center",
});

// Carte de succès
const SuccessCard = styled(motion.div)({
  position: "relative",
  overflow: "hidden",
  maxWidth: 620,
  margin: "0 auto",
  padding: "60px 40px",
  textAlign: "center",
  backgroundColor: "rgba(255,255,255,0.94)",
  border: "1px solid rgba(191,160,106,0.32)",
  borderRadius: 22,
  boxShadow: "0 24px 60px rgba(58,53,47,0.12)",
  "@supports ((backdrop-filter: blur(1px)) or (-webkit-backdrop-filter: blur(1px)))": {
    backgroundColor: "rgba(255,255,255,0.6)",
    backdropFilter: "blur(16px)",
    WebkitBackdropFilter: "blur(16px)",
  },
});

const Closing = styled(motion.div)({
  maxWidth: 620,
  margin: "70px auto 0",
  textAlign: "center",
  fontFamily: fonts.serif,
  fontStyle: "italic",
  fontSize: "1.25rem",
  lineHeight: 1.8,
  color: palette.goldDeep,
});

// ── Cœurs flottants (succès) ──────────────────────────────────

function SuccessParticles() {
  const hearts = Array.from({ length: 14 }).map((_, i) => ({
    left: 6 + (i * 6.5) % 88,
    delay: (i % 7) * 0.25,
    size: 12 + (i % 4) * 5,
    dur: 3 + (i % 5) * 0.6,
  }));
  return (
    <Box aria-hidden sx={{ position: "absolute", inset: 0, pointerEvents: "none", overflow: "hidden" }}>
      {hearts.map((h, i) => (
        <motion.div
          key={i}
          initial={{ y: "100%", opacity: 0, scale: 0.6 }}
          animate={{ y: "-30%", opacity: [0, 1, 1, 0], scale: 1 }}
          transition={{ duration: h.dur, delay: h.delay, ease: "easeOut" }}
          style={{ position: "absolute", bottom: 0, left: `${h.left}%`, color: palette.gold }}
        >
          <Heart size={h.size} fill={palette.gold} strokeWidth={0} />
        </motion.div>
      ))}
    </Box>
  );
}

// ── Variants ──────────────────────────────────────────────────

const stagger: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.16, delayChildren: 0.1 } },
};
const itemV: Variants = {
  hidden: { opacity: 0, y: 22, filter: "blur(6px)" },
  visible: { opacity: 1, y: 0, filter: "blur(0px)", transition: { duration: 0.7, ease: EASE } },
};

// ── Composant ─────────────────────────────────────────────────

export default function Rsvp() {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    name: "",
    attend: "",
    message: "",
    withGuest: "non",
    guestName: "",
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (field: string, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!form.name || !form.attend) {
      setError(t("rsvp.errorRequired"));
      return;
    }
    setLoading(true);
    setError("");

    const { error: sbError } = await supabase.from("rsvp").insert([
      {
        name: form.name,
        attend: form.attend,
        message: form.message,
        with_guest: form.withGuest === "oui",
        guest_name: form.withGuest === "oui" ? form.guestName : null,
      },
    ]);

    setLoading(false);
    if (sbError) {
      setError(t("rsvp.errorGeneric"));
    } else {
      setSuccess(true);
      setForm({ name: "", attend: "", message: "", withGuest: "non", guestName: "" });
    }
  };

  const radioSx = {
    color: palette.gold,
    transition: "transform .25s cubic-bezier(.16,1,.3,1)",
    "&.Mui-checked": { color: palette.goldDeep, transform: "scale(1.15)" },
  };

  const menuProps = {
    TransitionProps: { timeout: 280 },
    PaperProps: {
      sx: {
        mt: 1,
        borderRadius: 2,
        border: "1px solid rgba(191,160,106,0.3)",
        boxShadow: "0 14px 34px rgba(58,53,47,0.16)",
        "& .MuiMenuItem-root": {
          fontFamily: fonts.serif,
          fontSize: "0.98rem",
          transition: "background-color .2s ease",
          "&:hover": { backgroundColor: "rgba(191,160,106,0.12)" },
          "&.Mui-selected": { backgroundColor: "rgba(191,160,106,0.18)" },
          "&.Mui-selected:hover": { backgroundColor: "rgba(191,160,106,0.24)" },
        },
      },
    },
  };

  return (
    <Section>
      <Bloom />
      <Petals count={10} opacity={0.34} />

      <Container>
        {/* En-tête */}
        <Header
          variants={stagger}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.4 }}
        >
          <motion.div
            variants={itemV}
            style={{ display: "inline-flex", color: palette.gold, marginBottom: 8 }}
          >
            <motion.span
              whileInView={{ scale: [0.8, 1.18, 1] }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, ease: EASE }}
              style={{ display: "inline-flex", filter: "drop-shadow(0 0 8px rgba(191,160,106,0.6))" }}
            >
              <Heart size={26} fill={palette.gold} strokeWidth={1} />
            </motion.span>
          </motion.div>

          <motion.div variants={itemV}>
            <Title>
              <SplitText text={t("rsvp.title")} stagger={0.08} />
            </Title>
          </motion.div>

          <motion.div
            variants={itemV}
            style={{
              height: 2,
              width: 120,
              margin: "16px auto 0",
              background: "linear-gradient(90deg, transparent, #BFA06A, transparent)",
            }}
          />

          <motion.div variants={itemV}>
            <Subtitle>{t("rsvp.subtitle", { date: t("rsvp.deadline") })}</Subtitle>
          </motion.div>
        </Header>

        <AnimatePresence mode="wait">
          {success ? (
            <SuccessCard
              key="success"
              initial={{ opacity: 0, scale: 0.94 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, ease: EASE }}
            >
              <SuccessParticles />
              <motion.div
                initial={{ scale: 0, rotate: -20 }}
                animate={{ scale: [0, 1.2, 1], rotate: 0 }}
                transition={{ duration: 0.8, ease: EASE }}
                style={{
                  width: 72,
                  height: 72,
                  borderRadius: "50%",
                  margin: "0 auto 22px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "linear-gradient(135deg, #8FB08A, #6E9269)",
                  color: "#fff",
                  boxShadow: "0 10px 26px rgba(110,146,105,0.4)",
                }}
              >
                <Check size={34} strokeWidth={2.4} />
              </motion.div>
              <Typography sx={{ fontFamily: fonts.display, fontSize: "1.9rem", color: palette.ink, mb: 1.5 }}>
                {t("rsvp.successTitle")}
              </Typography>
              <Flourish width={160} />
              <Typography sx={{ fontFamily: fonts.serif, fontSize: "1.1rem", lineHeight: 1.85, color: palette.warmGray, maxWidth: 440, mx: "auto", mt: 1 }}>
                {t("rsvp.successBody")}
              </Typography>
            </SuccessCard>
          ) : (
            <FormCard
              key="form"
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.9, delay: 0.2, ease: EASE }}
            >
              {error && <ErrorMsg role="alert">{error}</ErrorMsg>}

              <TwoCol>
                <LeftCol>
                  <StyledTextField
                    label={t("rsvp.nameLabel")}
                    variant="outlined"
                    size="small"
                    value={form.name}
                    onChange={(e) => handleChange("name", e.target.value)}
                  />

                  <FormControl size="small">
                    <InputLabel sx={{ fontFamily: fonts.sans, fontSize: "0.8rem", letterSpacing: "0.05em" }}>
                      {t("rsvp.attendLabel")}
                    </InputLabel>
                    <Select
                      sx={selectSx}
                      label={t("rsvp.attendLabel")}
                      value={form.attend}
                      onChange={(e) => handleChange("attend", e.target.value as string)}
                      MenuProps={menuProps as Record<string, unknown>}
                    >
                      <MenuItem value="ceremonie">{t("rsvp.attendCeremony")}</MenuItem>
                      <MenuItem value="reception">{t("rsvp.attendReception")}</MenuItem>
                      <MenuItem value="les_deux">{t("rsvp.attendBoth")}</MenuItem>
                      <MenuItem value="absent">{t("rsvp.attendAbsent")}</MenuItem>
                    </Select>
                  </FormControl>

                  <GuestBox>
                    <GuestTitle>{t("rsvp.guestQuestion")}</GuestTitle>
                    <RadioGroup
                      row
                      value={form.withGuest}
                      onChange={(e) => handleChange("withGuest", e.target.value)}
                    >
                      <FormControlLabel
                        value="oui"
                        control={<Radio size="small" sx={radioSx} />}
                        label={<Typography sx={{ fontFamily: fonts.serif, fontSize: "0.95rem" }}>{t("rsvp.yes")}</Typography>}
                      />
                      <FormControlLabel
                        value="non"
                        control={<Radio size="small" sx={radioSx} />}
                        label={<Typography sx={{ fontFamily: fonts.serif, fontSize: "0.95rem" }}>{t("rsvp.no")}</Typography>}
                      />
                    </RadioGroup>

                    <AnimatePresence>
                      {form.withGuest === "oui" && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.4, ease: EASE }}
                          style={{ overflow: "hidden" }}
                        >
                          <Box sx={{ mt: 1.5 }}>
                            <StyledTextField
                              label={t("rsvp.guestNameLabel")}
                              variant="outlined"
                              size="small"
                              fullWidth
                              value={form.guestName}
                              onChange={(e) => handleChange("guestName", e.target.value)}
                            />
                          </Box>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </GuestBox>
                </LeftCol>

                <RightCol>
                  <MessageField
                    label={t("rsvp.messageLabel")}
                    variant="outlined"
                    multiline
                    rows={form.withGuest === "oui" ? 12 : 8}
                    value={form.message}
                    onChange={(e) => handleChange("message", e.target.value)}
                    sx={{ height: "100%" }}
                  />
                </RightCol>
              </TwoCol>

              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <SubmitBtn
                  type="button"
                  onClick={handleSubmit}
                  disabled={loading}
                  whileHover={{ scale: 1.04 }}
                  whileTap={{ scale: 0.96 }}
                  transition={{ type: "spring", stiffness: 320, damping: 18 }}
                >
                  {loading ? (
                    <CircularProgress size={18} sx={{ color: "#fff" }} />
                  ) : (
                    t("rsvp.submit")
                  )}
                </SubmitBtn>
              </Box>
            </FormCard>
          )}
        </AnimatePresence>

        {/* Message de clôture émotionnel */}
        <Closing
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.5 }}
          transition={{ duration: 1.2, ease: EASE }}
        >
          <Flourish width={180} />
          {t("rsvp.closing")}
        </Closing>
      </Container>
    </Section>
  );
}
